// QR is encoded locally: capture URLs never go to an external image service.
type QrLibrary = Pick<typeof import('qrcode'), 'toCanvas'>;
const options = { width: 220, margin: 2, errorCorrectionLevel: 'M' } as const;
let libraryPromise: Promise<QrLibrary> | null = null;

function library(): Promise<QrLibrary> {
  if (libraryPromise) return libraryPromise;
  let timer: number | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = window.setTimeout(() => reject(new Error('QR_LIBRARY_TIMEOUT')), 8000);
  });
  const pending = Promise.race([
    import('qrcode').then(module => {
      const qr = (module as { default?: QrLibrary }).default ?? module;
      if (typeof qr.toCanvas !== 'function') throw new Error('QR_LIBRARY_INVALID');
      return qr;
    }), timeout,
  ]).catch(() => {
    if (libraryPromise === pending) libraryPromise = null;
    throw Object.assign(new Error('No se pudo cargar el generador QR. Reintentá o usá Cámara o Adjunto.'), { userFacing: true });
  }).finally(() => { if (timer !== undefined) window.clearTimeout(timer); });
  libraryPromise = pending;
  return pending;
}

function withSignal<T>(operation: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const abort = () => { signal.removeEventListener('abort', abort); reject(new DOMException('Solicitud cancelada', 'AbortError')); };
    operation.then(value => { signal.removeEventListener('abort', abort); resolve(value); }, error => { signal.removeEventListener('abort', abort); reject(error); });
    if (signal.aborted) abort(); else signal.addEventListener('abort', abort, { once: true });
  });
}

interface CapturaQrState {
  url: string | null;
  error: string | null;
  cargando: boolean;
  vencido: boolean;
  restante: string;
  _timer: number | null;
  _generation: number;
  _disposed: boolean;
  _request: AbortController | null;
  generar(): Promise<void>;
  destroy(): void;
  _contar(expiraUtc: string): void;
  $refs: Record<string, HTMLCanvasElement>;
  $nextTick?: () => Promise<void>;
}

export function capturaQr(pacienteId: string) {
  return {
    url: null, error: null, cargando: false, vencido: false, restante: '',
    _timer: null, _generation: 0, _disposed: false, _request: null,

    async generar(this: CapturaQrState) {
      if (this.cargando || this._disposed) return;
      const generation = ++this._generation;
      const current = () => !this._disposed && generation === this._generation;
      if (this._timer !== null) window.clearInterval(this._timer);
      this._timer = null; this.cargando = true; this.error = null;
      this.url = null; this.vencido = false; this.restante = '';
      let deadline: number | undefined, timedOut = false;
      try {
        // Acquire code before issuing a one-use session. A chunk failure must not
        // create a token or trigger an automatic replay of its POST.
        const qr = await library();
        if (!current()) return;
        const controller = new window.AbortController(); this._request = controller;
        deadline = window.setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
        const tokenInput = document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]');
        const data = await withSignal(fetch(`/pacientes/${encodeURIComponent(pacienteId)}/foto/qr`, {
          method: 'POST', credentials: 'same-origin',
          headers: { RequestVerificationToken: tokenInput?.value ?? '' }, signal: controller.signal,
        }).then(async response => {
          const body = await response.json().catch(() => null);
          if (!response.ok) throw Object.assign(new Error(body?.message || 'No se pudo generar el código.'), { userFacing: true });
          return body;
        }), controller.signal);
        if (!current()) return;
        if (!data || typeof data.url !== 'string' || !data.url || typeof data.expiraUtc !== 'string' || !Number.isFinite(Date.parse(data.expiraUtc)))
          throw new Error('QR_RESPONSE_INVALID');
        this.url = data.url;
        // Alpine may need to mount the canvas after revealing the URL.
        if (this.$nextTick) await this.$nextTick(); else await Promise.resolve();
        if (!current()) return;
        const canvas = this.$refs.qr;
        if (!canvas) throw new Error('QR_CANVAS_MISSING');
        await qr.toCanvas(canvas, data.url, options);
        if (current()) this._contar(data.expiraUtc);
      } catch (failure) {
        if (current()) {
          this.url = null;
          this.error = timedOut ? 'No pudimos confirmar el QR a tiempo. Reintentá o usá Cámara o Adjunto.'
            : (failure as { userFacing?: boolean })?.userFacing ? (failure as Error).message
              : 'No se pudo generar el código. Probá de nuevo o usá Cámara o Adjunto.';
        }
      } finally {
        if (deadline !== undefined) window.clearTimeout(deadline);
        if (current()) { this._request = null; this.cargando = false; }
      }
    },

    _contar(this: CapturaQrState, expiraUtc: string) {
      if (this._timer !== null) window.clearInterval(this._timer);
      this._timer = null;
      const expires = Date.parse(expiraUtc);
      const tick = () => {
        const seconds = Math.floor((expires - Date.now()) / 1000);
        if (!Number.isFinite(seconds) || seconds <= 0) {
          this.vencido = true; this.restante = '0:00';
          if (this._timer !== null) window.clearInterval(this._timer);
          this._timer = null;
        } else this.restante = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
      };
      tick();
      if (!this.vencido && !this._disposed) this._timer = window.setInterval(tick, 1000);
    },

    // Alpine invokes destroy when its owner is removed, including HTMX swaps.
    destroy(this: CapturaQrState) {
      this._disposed = true; ++this._generation; this._request?.abort(); this._request = null;
      if (this._timer !== null) window.clearInterval(this._timer);
      this._timer = null; this.cargando = false; this.url = null;
    },
  };
}

(window as any).capturaQr = capturaQr;
(window as any).ProfintCaptureQr = {
  ready: () => library().then(() => undefined),
  render: (canvas: HTMLCanvasElement, url: string) => library().then(qr => qr.toCanvas(canvas, url, options)),
};
